/**
 * Amplitude event taxonomy — the SINGLE source of truth for event names.
 *
 * The old Mucka's analytics broke because event names were free-text at the
 * call site, so the same logical event got logged three different ways and
 * funnels never lined up. Here every event name is a named constant; call
 * sites pass `ANALYTICS_EVENTS.JOB_CREATED`, never the string. If you need a
 * new event, add it here first.
 *
 * Naming convention: "Object Action" in Title Case (Amplitude house style),
 * e.g. `Quote Approved`. Property keys are snake_case, values primitive.
 *
 * PII RULE: never put personal data (client names, addresses, emails, phone
 * numbers, raw money amounts tied to a person) into event properties. Use
 * ids, types, counts and enums. See docs/amplitude-analytics-plan.md §3.
 */

export const ANALYTICS_EVENTS = {
  // ── Activation / onboarding funnel ──────────────────────────────────
  SIGNUP_STARTED: 'Signup Started',
  SIGNUP_COMPLETED: 'Signup Completed',
  // Password-step friction (fired only on the public signup flow). Since the
  // trade gate landed, SIGNUP_STARTED marks reaching the TRADE step, not the
  // password one — so the gap between STARTED and COMPLETED is now trade
  // drop-off AND password drop-off. Subtract SIGNUP_TRADE_BLOCKED to isolate
  // the password half. These two make what's left explainable:
  //  - REQUIREMENTS_MET fires once when all five checks (4 composition + breach)
  //    first pass. Started → Requirements Met = struggled to form a valid
  //    password; Requirements Met → Completed = dropped for another reason.
  //  - BREACHED fires each time the live HaveIBeenPwned check rejects their
  //    chosen password. High volume here + low completion = the breach guard is
  //    annoying people. `breach_hits` on the later events lets you segment by it.
  SIGNUP_PASSWORD_REQUIREMENTS_MET: 'Signup Password Requirements Met',
  SIGNUP_PASSWORD_BREACHED: 'Signup Password Breached',
  // The trade gate (2026-08). Fires when the trade someone picked isn't one we
  // serve: they go on the waiting list and NO account is created, so they can
  // never reach SIGNUP_COMPLETED. The gate is the SECOND step, so the funnel
  // reads: STARTED = BLOCKED + COMPLETED + everyone who dropped at the picker
  // or the password.
  //
  // There is deliberately no matching "trade step viewed" event. The gate moved
  // to sit straight after the details step, so SIGNUP_STARTED already marks
  // that exact moment — a second event on the same trigger is two names for one
  // number, which is how the old Mucka's funnels stopped lining up.
  //
  // 🚨 Don't read a fall in SIGNUP_COMPLETED as a regression once this is live.
  // The gate is a deliberate filter — turning away a roofer is the feature
  // working. BLOCKED carries `trade`, which is the demand signal that decides
  // which trade we open up to next.
  SIGNUP_TRADE_BLOCKED: 'Signup Trade Blocked',
  // Fired once, the first time the onboarding flow is actually presented to a
  // user. Lets us split "signed up but never shown onboarding" (SIGNUP_COMPLETED
  // with no ONBOARDING_STARTED) from "shown onboarding but dropped"
  // (ONBOARDING_STARTED with no ONBOARDING_COMPLETED).
  ONBOARDING_STARTED: 'Onboarding Started',
  ONBOARDING_STEP_VIEWED: 'Onboarding Step Viewed',
  // Fired when a user advances OFF a step (forward navigation only). Paired
  // with ONBOARDING_STEP_VIEWED this gives a true step-by-step funnel: viewed
  // minus completed on the same step = who abandoned there.
  ONBOARDING_STEP_COMPLETED: 'Onboarding Step Completed',
  ONBOARDING_COMPLETED: 'Onboarding Completed',
  // Team businesses (any non-solo team size on the company-details step) are
  // routed to a "book an intro call" dead-end instead of the self-serve flow.
  // GATE = the screen was shown; BOOK_CLICKED = they tapped through to the
  // HubSpot scheduler. Both carry team_size so we can see conversion per bucket.
  ONBOARDING_TEAM_CALL_GATE: 'Onboarding Team Call Gate',
  ONBOARDING_BOOK_CALL_CLICKED: 'Onboarding Book Call Clicked',
  // Backed out of the book-a-call screen (the chevron) rather than booking. The
  // gate is deliberately soft, so this measures how leaky it is: gate fired minus
  // this = the team leads that actually stuck. Note a back-tap is not itself an
  // escape — the form re-gates them unless they change their team size — so a
  // rise here without a matching fall in gated accounts just means people are
  // reviewing their answers.
  ONBOARDING_BOOK_CALL_DISMISSED: 'Onboarding Book Call Dismissed',
  // In-chat onboarding sub-funnel (step 5 — the OnboardingChatPanel). The
  // generic STEP_VIEWED/STEP_COMPLETED pair only tells us "entered step 5" vs
  // "left step 5"; these break the richest drop-off surface into its beats:
  // voice pick → bolt → first message → skills → WhatsApp gate.
  ONBOARDING_VOICE_CHOSEN: 'Onboarding Voice Chosen', // confirmed a voice/accent on the picker
  // Tapped the trainer screen's target to open the chat. That target is now Ask
  // Mucka on the bar, not the bolt — the WIRE NAME is deliberately left alone so
  // the funnel doesn't split at the rename. Don't "correct" it.
  ONBOARDING_BOLT_TAPPED: 'Onboarding Bolt Tapped',
  // Fired on every user turn in the onboarding chat. `input_mode` (text|voice)
  // is the primary signal for "who types vs who talks"; also mirrored to the
  // `onboarding_used_voice` USER property so any metric can be sliced by it.
  ONBOARDING_CHAT_MESSAGE_SENT: 'Onboarding Chat Message Sent',
  ONBOARDING_VOICE_MODE_STARTED: 'Onboarding Voice Mode Started', // switched the chat into a voice session
  ONBOARDING_SKILLS_OFFER_TAPPED: 'Onboarding Skills Offer Tapped', // took the "Show skills" shortcut instead of typing yes
  ONBOARDING_SKILLS_RECOMMENDED: 'Onboarding Skills Recommended', // agent surfaced the skill cards
  ONBOARDING_SKILLS_SELECTED: 'Onboarding Skills Selected', // confirmed skills (method: add_all|custom)
  ONBOARDING_WHATSAPP_PAIR_STARTED: 'Onboarding WhatsApp Pair Started', // pairing code dispatched
  ONBOARDING_WHATSAPP_PAIRED: 'Onboarding WhatsApp Paired', // pairing confirmed — the compulsory gate cleared
  // Variant B runs the WHOLE onboarding inside one ElevenLabs websocket, so a
  // dropped socket (backgrounding the app to fetch a WhatsApp code is the
  // commonest cause) used to strand the user with no way forward. These three
  // measure that: how often we lose the socket mid-flow, how often the resume
  // works, and how often someone has to fall back to the manual finish.
  ONBOARDING_CHAT_STALLED: 'Onboarding Chat Stalled', // socket dropped mid-flow; recovery UI shown
  ONBOARDING_CHAT_RESUMED: 'Onboarding Chat Resumed', // reconnected and carried on (source: auto|manual)
  ONBOARDING_MANUAL_FINISH: 'Onboarding Manual Finish', // finished without the agent calling finish_intro
  // Onboarding v3's fork and proper setup (docs/onboarding-v3-plan.md). Every
  // answer is also kept on onboarding_progress.setup_answers for Mission
  // Control; these are the same answers in Amplitude. All carry variant 'b'.
  ONBOARDING_PATH_CHOSEN: 'Onboarding Path Chosen', // the fork: path proper|straight
  ONBOARDING_SETUP_ANSWERED: 'Onboarding Setup Answered', // question + answer, one per answer given or skipped
  ONBOARDING_MIGRATION_REQUESTED: 'Onboarding Migration Requested', // kind app|email|spreadsheet, app
  ONBOARDING_ACCOUNTS_CONNECT: 'Onboarding Accounts Connect', // result started|connected|failed, package
  ONBOARDING_NEXT_JOB_ADDED: 'Onboarding Next Job Added', // path, jobs_added

  // ── Invited-member onboarding (/welcome) ────────────────────────────
  //
  // A SEPARATE namespace from ONBOARDING_*, deliberately. The owner funnel
  // charts on step_number out of ONBOARDING_TOTAL_STEPS, and folding a
  // four-screen member flow into it would silently move every owner
  // conversion rate the moment invited users started arriving. Different
  // audience, different screens, different denominator — different events.
  //
  // Started fires once per user, on first sight of the flow. Skipped is the
  // one worth watching: this flow is soft by design, so "asked and declined"
  // and "asked and paired" have to be tellable apart, or the phase-2 re-nag
  // has nothing to aim at. See docs/invited-user-onboarding.md.
  MEMBER_ONBOARDING_STARTED: 'Member Onboarding Started',
  MEMBER_ONBOARDING_STEP_VIEWED: 'Member Onboarding Step Viewed',
  MEMBER_ONBOARDING_STEP_COMPLETED: 'Member Onboarding Step Completed',
  MEMBER_ONBOARDING_STEP_SKIPPED: 'Member Onboarding Step Skipped',
  MEMBER_ONBOARDING_COMPLETED: 'Member Onboarding Completed',
  // Push is the rung that fails silently, so its outcome is measured
  // separately from the step funnel. DENIED is not a bug to chase — both
  // platforms suppress the runtime dialog for good after a permanent deny, so
  // the deny rate is the ceiling on how many members push can ever reach.
  MEMBER_ONBOARDING_PUSH_GRANTED: 'Member Onboarding Push Granted',
  MEMBER_ONBOARDING_PUSH_DENIED: 'Member Onboarding Push Denied',
  // Web members can't be prompted at all — there is no OS dialog in a browser
  // tab. This counts the only thing that CAN help them: tapping through to
  // install the real app.
  MEMBER_ONBOARDING_STORE_TAPPED: 'Member Onboarding Store Tapped',
  // Pairing, in the MEMBER namespace rather than reusing the owner flow's
  // ONBOARDING_WHATSAPP_* pair. Neither of those events carries a flow
  // dimension, so sharing them would silently fold member pairings into every
  // owner-onboarding chart built on them — the same reason the step events are
  // split. PAIR_STARTED = a code was dispatched; PAIRED = it was accepted, and
  // since this step is a compulsory gate, PAIRED is also the flow's real
  // completion rate. The gap between them is the lock-out: members who asked
  // for a code and never got past it.
  MEMBER_ONBOARDING_WHATSAPP_PAIR_STARTED: 'Member Onboarding WhatsApp Pair Started',
  MEMBER_ONBOARDING_WHATSAPP_PAIRED: 'Member Onboarding WhatsApp Paired',
  WORKSPACE_SET: 'Workspace Set',
  FIRST_JOB_CREATED: 'First Job Created',
  FIRST_QUOTE_SENT: 'First Quote Sent',
  FIRST_INVOICE_SENT: 'First Invoice Sent',

  // ── Core workflow ───────────────────────────────────────────────────
  JOB_CREATED: 'Job Created',
  JOB_STATUS_CHANGED: 'Job Status Changed',
  QUOTE_CREATED: 'Quote Created',
  QUOTE_SENT: 'Quote Sent',
  QUOTE_APPROVED: 'Quote Approved',
  QUOTE_REJECTED: 'Quote Rejected',
  INVOICE_CREATED: 'Invoice Created',
  INVOICE_SENT: 'Invoice Sent',
  INVOICE_PAID: 'Invoice Paid',
  CLIENT_CREATED: 'Client Created',
  SCHEDULE_EVENT_CREATED: 'Schedule Event Created',
  ENGINEER_ASSIGNED: 'Engineer Assigned',
  // One event for every control on the job page, desktop and phone
  // (docs/jobs-page-rebuild-plan.md). Fired from each action's ONE home by
  // `trackJobAction` (src/lib/jobs/track-job-action.ts), never by hand.
  // Properties are enums only — `action`, `route` (where the press happened),
  // `surface` ('desktop' | 'phone'), `stage` and `mode` ('working' |
  // 'watching'). Built for the one question nothing could answer before
  // 2026-10-30: which controls on the job page anyone actually uses.
  JOB_ACTION: 'Job Action',

  // ── AI engine (the trust-model funnel — highest value) ──────────────
  ENGINE_ACTION_SUGGESTED: 'Engine Action Suggested',
  ENGINE_ACTION_APPROVED: 'Engine Action Approved',
  ENGINE_ACTION_DISMISSED: 'Engine Action Dismissed',
  ENGINE_ACTION_EDITED: 'Engine Action Edited',
  // "No need, I've already sorted it." Neither an approval nor a rejection —
  // the observation was right and we simply weren't needed. Counting it as
  // either would misread the funnel; a rising rate here means Mucka is late to
  // a fact it could be learning earlier.
  ENGINE_ACTION_ALREADY_DONE: 'Engine Action Already Done',
  MUCKA_PANEL_OPENED: 'Mucka Panel Opened',
  VOICE_SESSION_STARTED: 'Voice Session Started',
  // Why a NATIVE (iOS/Android app) voice call ended, and how many times it
  // picked itself back up after a signal dip. Sent by the runtime's closing
  // request; before TKT-1462 the reason reached the user's screen and nobody else.
  VOICE_NATIVE_SESSION_ENDED: 'Voice Native Session Ended', // reason, reconnects
  CHAT_MESSAGE_SENT: 'Chat Message Sent',

  // ── WhatsApp assistant (usage + proactive-message performance) ──────
  // All fired server-side (webhook + dispatcher) — there is no client on
  // this surface. Funnels these unlock: proactive sent → replied/tapped
  // (does each ping type earn engagement); invoice_ready sent → preview
  // tapped → send tapped → Invoice Sent (the quote-to-cash loop); and
  // received-by-kind over time (who actually uses the assistant, and
  // whether they type, talk, or send photos).
  WHATSAPP_MESSAGE_RECEIVED: 'WhatsApp Message Received', // kind, button, tap_source
  WHATSAPP_BUTTON_TAPPED: 'WhatsApp Button Tapped', // button, tap_source — subset of RECEIVED, split out so funnels stay one-event-per-step
  WHATSAPP_REPLY_SENT: 'WhatsApp Reply Sent', // tools_used, tool_count, had_mutation, voice_reply — tools_used IS the "types of requests" signal
  WHATSAPP_PROACTIVE_DISPATCHED: 'WhatsApp Proactive Dispatched', // type, outcome, in_window, via_template — outcome includes the skip reasons, so fatigue/cap losses are visible

  // ── Skills / engagement ─────────────────────────────────────────────
  SKILL_ENABLED: 'Skill Enabled',
  SKILL_DISABLED: 'Skill Disabled',
  HELP_ARTICLE_VIEWED: 'Help Article Viewed',

  // ── Referral programme ──────────────────────────────────────────────
  // Fired when the user copies or shares their referral link from Settings →
  // Referrals (`method` = copy|share). Paired with signups carrying a
  // referral_code prop, this is the top of the referral funnel.
  REFERRAL_LINK_COPIED: 'Referral Link Copied',
  // Fired when the user successfully changes their personal share code.
  REFERRAL_CODE_CUSTOMISED: 'Referral Code Customised',
  // The scheduled Share & Earn reminder sheet (docs/share-and-earn-reminders-plan.md).
  // Each carries `ask` (1 = first reminder) and `owner`, so share rate per step
  // and per seat type can be read off and steps that earn nothing cut.
  SHARE_PROMPT_SHOWN: 'Share Prompt Shown',
  SHARE_PROMPT_SHARED: 'Share Prompt Shared',
  SHARE_PROMPT_DISMISSED: 'Share Prompt Dismissed',

  // ── Native app boot health ──────────────────────────────────────────
  // Fired once, on the load that FOLLOWS the app failing to reach the server
  // at launch and then recovering itself. The bundled Capacitor errorPath page
  // is a static file with no analytics, no Sentry and no origin to report to —
  // so a stranded launch was structurally invisible and we only ever learned
  // about it when a customer said something (Artic Cooling, #1427). This is
  // the report the page couldn't send, posted by the app the moment it's back.
  //
  // It measures RECOVERIES, not strandings: a launch that never recovers can't
  // report, so treat the count as a floor. `seconds_stranded` is the honest
  // severity signal — a 2s blip nobody noticed and a 40s one that cost a job
  // both land here, and only this tells them apart.
  NATIVE_BOOT_RECOVERED: 'Native Boot Recovered',

  // ── Asking our own customers to review Mucka ────────────────────────
  // Fired when an owner clicks through from the Trustpilot banner. WEB ONLY,
  // and it has no native twin on purpose: the store prompt reports nothing
  // back — not whether it appeared, not what was written — so there is no
  // equivalent moment to record.
  //
  // 🚨 Never divide reviews by asks. The ask count lives in `platform_events`
  // (`app_review_ask_shown`), the review count lives in the store dashboards,
  // and nothing connects a row in one to a row in the other. A ratio of the
  // two would be a number that can only agree with itself.
  APP_REVIEW_BANNER_CLICKED: 'App Review Banner Clicked',

  // ── App updates ─────────────────────────────────────────────────────
  //
  // 🚨 These exist because NOTHING about the updater is observable otherwise.
  // A rule that never fires and a rule that fires constantly look identical
  // from outside: the only symptom either way is a reload somebody noticed, so
  // the first report is a customer saying "it keeps refreshing" or "it lost my
  // quote". That is how a first-run gate reached 11 customers on 2026-09-10,
  // and how the login rule shipped able to fire exactly never.
  //
  // Pair them. DETECTED fires when a new version is parked; APPLIED when it is
  // taken, with the rule that took it. Detected-without-applied IS the
  // staleness measure, and `rule` is what makes the four thresholds tunable
  // rather than guessed.
  APP_UPDATE_DETECTED: 'App Update Detected',
  APP_UPDATE_APPLIED: 'App Update Applied',
  // A rule chose to apply an update, but the page was already on the waiting
  // worker's build, so there was nothing to reload for. Without it, a rule that
  // fires and is skipped reads exactly like a rule that never fired.
  APP_UPDATE_NOT_NEEDED: 'App Update Not Needed',
  // ── Search ──────────────────────────────────────────────────────────
  /**
   * One global search that produced an answer, fired server-side so it covers
   * every caller (desktop Topbar, mobile search, anything later) rather than
   * being wired into each.
   *
   * 🚨 NEVER the raw term. It is customer names, addresses and phone numbers —
   * exactly what the PII rule above forbids. What is sent is the SHAPE of the
   * search: how long it was, how many words, how many rows each section
   * returned, and whether the whole thing came back empty.
   *
   * The point of it is `found_nothing`. Three separate matching faults ran in
   * production for months and reached us only as "search doesn't work",
   * because nothing counted how often it failed. A zero-result rate is the
   * number that would have said so.
   */
  SEARCH_PERFORMED: 'Search Performed',

  // ── Online booking (the public /book/<handle> page) ──
  // Server-side, because the page is opened by a stranger with no session.
  // Never the customer's details — the account, the mode and the outcome.
  ONLINE_BOOKING_PAGE_VIEWED: 'Online Booking Page Viewed',
  ONLINE_BOOKING_REQUESTED: 'Online Booking Requested',
  ONLINE_BOOKING_ENQUIRY: 'Online Booking Enquiry',
} as const;

/** Union of every valid event name. `track()` only accepts these. */
export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Flat list of names — used by tests to assert uniqueness. */
export const ALL_EVENT_NAMES: AnalyticsEventName[] = Object.values(ANALYTICS_EVENTS);

/**
 * Permitted event-property value types. Kept primitive on purpose: Amplitude
 * charts work best with scalars/enums, and it keeps PII out by construction
 * (no nested objects where a stray client record could hide).
 */
export type EventPropertyValue = string | number | boolean | string[] | undefined;
export type EventProperties = Record<string, EventPropertyValue>;

/**
 * Canonical ordered step names for the OWNER onboarding flow
 * (src/app/onboarding/OnboardingFlowB.tsx). Index === the flow's internal step
 * number, so the array is the single source of truth mapping a step index to
 * its analytics `step_name`. Keep in lock-step with the flow's render branches
 * — if you insert/reorder a step, update this list so the funnel labels don't
 * silently drift.
 *
 * This WAS variant B of the 2026-07/08 onboarding A/B test. B won on structure
 * (see docs/onboarding-ab-test-results.md), A was deleted on 2026-08-17, and
 * these are now simply the onboarding steps.
 */
export const ONBOARDING_STEP_NAMES = [
  'welcome_carousel', // 0
  'meet_mucka', // 1 — voice + accent picker
  'personality', // 2 — name + personality + customer tone
  'chat_intro', // 3 — press-the-bolt training
  'onboarding_chat', // 4 — full conversational onboarding
  'preparing_workspace', // 5 — completion marker
] as const;

/** Total number of steps in the owner onboarding flow. */
export const ONBOARDING_TOTAL_STEPS = ONBOARDING_STEP_NAMES.length;

/**
 * Steps in the INVITED-MEMBER flow (/welcome). Kept apart from
 * ONBOARDING_STEP_NAMES for the reason given on the MEMBER_ONBOARDING_*
 * events: shared indices across two different flows make every funnel
 * built on either one wrong.
 */
export const MEMBER_ONBOARDING_STEP_NAMES = [
  'member_carousel', // 0 — no progress bar
  'member_accent', // 1 — voice + accent picker (per-user, not per-account)
  'member_whatsapp', // 2 — pairing, and the only place an invitee is asked for a number
  'member_notifications', // 3 — OS permission on native, install-the-app on web
] as const;

export const MEMBER_ONBOARDING_TOTAL_STEPS = MEMBER_ONBOARDING_STEP_NAMES.length;

/**
 * The RETIRED variant-A step list. Nothing writes these any more — kept because
 * `onboarding_progress` holds A rows forever and the super-admin funnel has to
 * render them with the right labels in the right order. Delete this and every
 * account that onboarded before 2026-08-17 renders as "unknown step".
 *
 * Also the compatibility target for the deploy window: a browser still holding
 * the old A bundle keeps POSTing A step indices for a few minutes after the
 * cutover, and those must land against the list they were numbered for.
 */
export const ONBOARDING_STEP_NAMES_LEGACY_A = [
  'welcome_carousel', // 0
  'company_details', // 1
  'pain_points', // 2
  'meet_mucka', // 3 — voice + accent picker
  'whatsapp_pair', // 4 — compulsory WhatsApp connection
  'chat_intro', // 5 — press-the-bolt training
  'onboarding_chat', // 6
  'preparing_workspace', // 7 — completion marker
] as const;

/**
 * Steps in the SHORT owner flow — the 2026-09 test's B arm
 * (src/app/onboarding/OnboardingFlowNew.tsx).
 *
 * The conversation is gone from onboarding entirely: it became "Set up with
 * Mucka", launched from the Get Started surface once the owner is inside the
 * product. What is left is the visual run-up plus pairing.
 *
 * Kept as its own array rather than a slice of ONBOARDING_STEP_NAMES because
 * the indices genuinely disagree — 3 is `chat_intro` there and `whatsapp_pair`
 * here — and a funnel built on a shared index would be wrong for one of the two
 * arms without ever looking wrong.
 */
export const ONBOARDING_STEP_NAMES_SHORT = [
  'welcome_carousel', // 0
  'meet_mucka', // 1 — voice + accent picker
  'personality', // 2 — name + personality + customer tone
  'whatsapp_pair', // 3 — pairing, back in the flow and required
  // 4–11: onboarding v3 Part 2 (docs/onboarding-v3-plan.md). The fork, then
  // "Set me up properly" (5–9, each skippable) or straight to the job.
  'start_fork', // 4 — Set me up properly / Just let me in
  'business_name', // 5
  'business_address', // 6
  'vat', // 7
  'work_now', // 8 — where their customers and jobs are now
  'accounts_software', // 9
  // 10: "Who's on the team?", only for owners who said at sign-up there's more
  // than one of them; after Accounts in proper setup, after the fork otherwise.
  'add_team', // 10
  'next_job', // 11 — try me with your next job
  'job_added', // 12
  'preparing_workspace', // 13 — completion marker
] as const;

export const ONBOARDING_SHORT_TOTAL_STEPS = ONBOARDING_STEP_NAMES_SHORT.length;

/**
 * Resolve the step-name array + total for a progress ping.
 *
 * 🚨 The LETTER IS NOT ENOUGH. 'a' and 'b' have been used twice, for four
 * different flows:
 *
 *   assignedAt null (the closed 2026-07/08 test)
 *     'a' → the form-led flow, deleted 2026-08-17 → LEGACY_A
 *     'b' → the voice-led flow                    → ONBOARDING_STEP_NAMES
 *
 *   assignedAt set (the 2026-09 test)
 *     'a' → that same voice-led flow, in reserve  → ONBOARDING_STEP_NAMES
 *     'b' → the short flow                        → ..._SHORT
 *
 * Reading the letter alone renders every new A account with the retired flow's
 * screen names, and quietly merges the two eras' B arms into one funnel. The
 * era marker is `accounts.onboarding_variant_assigned_at`, mirrored onto
 * `onboarding_progress.variant_assigned_at` so this can be answered without a
 * join.
 *
 * Unknown letter → the voice-led list, which is the arm every unstamped account
 * has actually walked since August.
 */
export function onboardingStepMeta(
  variant?: 'a' | 'b' | null,
  assignedAt?: string | Date | null,
): {
  names: readonly string[];
  total: number;
} {
  const currentEra = assignedAt != null;

  if (currentEra && variant === 'b') {
    return { names: ONBOARDING_STEP_NAMES_SHORT, total: ONBOARDING_SHORT_TOTAL_STEPS };
  }
  if (!currentEra && variant === 'a') {
    return { names: ONBOARDING_STEP_NAMES_LEGACY_A, total: ONBOARDING_STEP_NAMES_LEGACY_A.length };
  }
  return { names: ONBOARDING_STEP_NAMES, total: ONBOARDING_TOTAL_STEPS };
}

/**
 * Typed property shape for the onboarding step funnel events
 * (ONBOARDING_STEP_VIEWED / ONBOARDING_STEP_COMPLETED). Enforced at the call
 * site so the funnel always has consistent, chartable dimensions.
 *
 * `step` is the legacy 0-indexed value kept for continuity with existing
 * charts; `step_number` is the human 1-indexed position (1..total_steps).
 */
export interface OnboardingStepProperties extends EventProperties {
  step: number;
  step_name: string;
  step_number: number;
  total_steps: number;
  flow_type: 'owner' | 'invited';
  /** A/B arm ('a' | 'b') so every step event segments by variant. */
  variant?: 'a' | 'b';
}

/**
 * Typed property shape for CLIENT_CREATED. Carries the activation context we
 * use to analyse what drives a business's FIRST client (their key activation
 * moment). No PII — ids/enums/durations only.
 */
export interface ClientCreatedProperties extends EventProperties {
  client_type: string;
  is_factor: boolean;
  /** How the client record came to exist. */
  created_via: 'manual' | 'import' | 'template' | 'zapier' | 'phone_contacts' | 'highlevel';
  /** True when this is the account's first real (non-demo) client. */
  is_first_client: boolean;
  /** Seconds between the acting user's signup and this creation. */
  time_since_signup_seconds?: number;
}
